import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../Icon';
import styles from './Dropdown.module.css';
import cn from 'classnames';

type Option = {
  value: string;
  label: string;
  description?: string;
};

interface DropdownProps {
  options: Option[];
  placeholder?: string;
  onSelect?: (selectedOption: Option | null) => void;
  selected?: Option | null;
  onChange?: (value: string) => void;
  className?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  placeholder = 'Не имеет значения',
  onSelect,
  selected = null,
  onChange,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>(selected);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleOptionClick = (option: Option) => {
    setSelectedOption(option);
    setIsOpen(false);
    if (onSelect) {
      onSelect(option);
    }

    if (onChange) {
      onChange(option.value);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setSelectedOption(selected);
  }, [selected]);

  return (
    <div ref={dropdownRef} className={styles.dropdown}>
      <div
        className={cn('text_subheaders_sh3', styles.dropdown_header, className, isOpen && styles.open)}
        onClick={toggleDropdown}
      >
        {selectedOption ? selectedOption.label : placeholder}
        <Icon name='dropdownIcon' width='13' height='8' className={isOpen ? styles.rotated : ''} />
      </div>
      <ul className={cn(styles.dropdown_list, isOpen && styles.open)}>
        {options.map((option) => (
          <li
            key={option.value}
            className={styles.dropdown_item}
            onClick={() => handleOptionClick(option)}
          >
            <div className={styles.option_content}>
              <span className='text_body_b3'>{option.label}</span>
              <span
                className={cn(styles.circle, selectedOption?.value === option.value && styles.selected)}
              ></span>
            </div>
            {option.description && <span className={cn(styles.option_description, 'text_captions_c1')}>{option.description}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Dropdown;
